import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";

const Shop = () => {
    const [products, setProducts] = useState([]);

    useEffect(() => {
        getProducts().then(setProducts).catch(() => {});
    }, []);

    // Best sellers first, then the rest — same natural order as before, quietly applied.
    const sorted = [...products].sort(
        (a, b) => (b.best_seller ? 1 : 0) - (a.best_seller ? 1 : 0)
    );

    return (
        <div data-testid="shop-page" className="bg-[#FAF9F6]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20">
                <p className="label">Shop</p>
                <h1
                    className="font-serif text-5xl sm:text-6xl mt-5 text-[#1C1C1A] leading-none"
                    data-testid="shop-heading"
                >
                    Handcrafted Tallow Soap
                </h1>
                <p className="mt-5 max-w-2xl text-[#4A4A46] leading-relaxed">
                    Every ALATI bar begins the same way &mdash; with thoughtfully chosen
                    grass-fed tallow, slowly prepared oils and recipes inspired by
                    generations of traditional soap making. Each bar simply tells a
                    different story through the ingredients we choose to add.
                </p>
                <p
                    className="mt-6 font-serif italic text-lg sm:text-xl text-[#1C1C1A] max-w-2xl leading-snug"
                    data-testid="collection-philosophy"
                >
                    Every bar begins the same way.
                    <br />
                    The story changes with the ingredients.
                </p>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
                {sorted.length === 0 ? (
                    <p className="text-[#4A4A46]" data-testid="shop-empty">
                        The collection will be here shortly. Check back soon.
                    </p>
                ) : (
                    <div
                        className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-14"
                        data-testid="product-grid"
                    >
                        {sorted.map((p) => (
                            <ProductCard key={p.slug} product={p} />
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-[#F0EBE1] py-20 px-6 lg:px-10 mt-4">
                <div className="max-w-4xl mx-auto text-center">
                    <p className="label">Build Your Own Bundle</p>
                    <h2 className="font-serif text-3xl sm:text-4xl mt-5 leading-snug">
                        Choose a few for home.
                        <br />
                        Choose a few for gifting.
                    </h2>
                </div>
                <div className="max-w-4xl mx-auto mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="bundle-options">
                    <Link
                        to="/bundles"
                        data-testid="bundle-option-3"
                        className="group block border border-[#C7C0B3] bg-[#FAF9F6] p-8 text-center transition-colors duration-300 hover:bg-[#1C1C1A] hover:text-[#FAF9F6] hover:border-[#1C1C1A]"
                    >
                        <p className="label group-hover:text-[#C7C0B3]">Bundle of Three</p>
                        <p className="font-serif text-4xl mt-4">Any 3 bars</p>
                        <p className="font-serif text-3xl mt-4">$63.75 AUD</p>
                        <p className="mt-3 text-xs uppercase tracking-[0.22em] text-[#6A7059] group-hover:text-[#C7C0B3]">
                            Save 15%
                        </p>
                    </Link>
                    <Link
                        to="/bundles"
                        data-testid="bundle-option-5"
                        className="group block border border-[#C7C0B3] bg-[#FAF9F6] p-8 text-center transition-colors duration-300 hover:bg-[#1C1C1A] hover:text-[#FAF9F6] hover:border-[#1C1C1A]"
                    >
                        <p className="label group-hover:text-[#C7C0B3]">Bundle of Five</p>
                        <p className="font-serif text-4xl mt-4">Any 5 bars</p>
                        <p className="font-serif text-3xl mt-4">$100 AUD</p>
                        <p className="mt-3 text-xs uppercase tracking-[0.22em] text-[#6A7059] group-hover:text-[#C7C0B3]">
                            Save 20%
                        </p>
                    </Link>
                </div>
                <p className="mt-10 text-sm text-[#737562] text-center max-w-xl mx-auto">
                    Bundle pricing is applied automatically at checkout when the right number
                    of bars is in your cart.
                </p>
            </div>

            <div className="py-20 px-6 lg:px-10" data-testid="artisan-slab-section">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="label">Artisan Soap Slabs</p>
                        <h2 className="font-serif text-3xl sm:text-4xl mt-5 leading-snug">
                            Something made on a larger scale.
                        </h2>
                        <p className="mt-5 text-[#4A4A46] leading-relaxed max-w-2xl mx-auto">
                            A slab is twenty-five bars from a single batch &mdash; for the home,
                            for gifting, for a business order. Cut and wrapped by hand.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div
                            className="border border-[#C7C0B3] bg-[#FAF9F6] p-8"
                            data-testid="artisan-slab-alati"
                        >
                            <p className="label">ALATI Artisan Slab</p>
                            <h3 className="font-serif text-2xl mt-4 leading-snug">
                                25 bars of one ALATI recipe.
                            </h3>
                            <p className="mt-4 text-sm text-[#4A4A46] leading-relaxed">
                                Choose any existing ALATI bar. We make a full slab in a single
                                batch, cure it slowly, and cut twenty-five bars by hand.
                            </p>
                            <p className="font-serif text-2xl mt-6 text-[#1C1C1A]">
                                $500 AUD <span className="text-sm text-[#737562]">+ shipping</span>
                            </p>
                        </div>
                        <div
                            className="border border-[#C7C0B3] bg-[#FAF9F6] p-8"
                            data-testid="artisan-slab-custom"
                        >
                            <p className="label">Custom Artisan Slab</p>
                            <h3 className="font-serif text-2xl mt-4 leading-snug">
                                25 bars, made to your requirements.
                            </h3>
                            <p className="mt-4 text-sm text-[#4A4A46] leading-relaxed">
                                Your choice of botanical infusion. Your choice of essential oil
                                blend, or unscented. Selected premium butters where appropriate.
                            </p>
                            <p className="font-serif text-2xl mt-6 text-[#1C1C1A]">
                                From $550 AUD{" "}
                                <span className="text-sm text-[#737562]">+ shipping</span>
                            </p>
                        </div>
                    </div>
                    <div className="mt-10 text-center">
                        <Link
                            to="/contact?subject=Artisan%20Slab%20Enquiry"
                            className="btn-primary"
                            data-testid="artisan-slab-enquiry-link"
                        >
                            Enquire About an Artisan Slab
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Shop;
